import React from 'react';
import {
  Box,
  ListItem,
  Typography,
  Paper,
  Chip,
  Card,
  CardContent,
  Link,
} from '@mui/material';

const formatCurrency = (num) => {
  if (num === null || num === undefined) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(num);
};

const formatNumber = (num, decimals = 2) => {
  if (num === null || num === undefined) return '-';
  return Number(num).toLocaleString(undefined, { maximumFractionDigits: decimals });
};

const formatPercent = (num) => {
  if (num === null || num === undefined) return '-';
  return `${Number(num).toFixed(2)}%`;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleString();
  } catch {
    return dateString;
  }
};

const priorityColor = (priority) => {
  if (!priority) return 'default';
  const p = String(priority).toUpperCase();
  if (p === 'P0') return 'error';
  if (p === 'P1') return 'warning';
  return 'default';
};

const notificationTypeColor = (type) => {
  if (!type) return 'default';
  if (type.startsWith('convergence')) return 'success';
  if (type === 'whale_move') return 'info';
  if (type === 'sell_signal') return 'error';
  return 'default';
};

const MetricRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 500 }}>{value}</Typography>
  </Box>
);

const AlphaNotificationCard = ({ hit }) => {
  const sig = hit._source || {};
  const token = sig.token || {};
  const summary = sig.summary || {};
  const traders = Array.isArray(sig.traders) ? sig.traders : [];
  const reasons = Array.isArray(sig.why?.reasons) ? sig.why.reasons : [];

  return (
    <ListItem
      sx={{
        borderBottom: '1px solid #e0e0e0',
        flexDirection: 'column',
        alignItems: 'stretch',
        py: 2,
        px: 0,
      }}
    >
      <Card elevation={1} sx={{ width: '100%', backgroundColor: '#fafafa', overflow: 'visible' }}>
        <CardContent sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3, mb: 0.5 }}>
                {sig.headline || token.symbol || sig.signal_id || 'Untitled signal'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
                {sig.priority && (
                  <Chip size="small" label={sig.priority} color={priorityColor(sig.priority)} />
                )}
                {sig.notification_type && (
                  <Chip
                    size="small"
                    label={sig.notification_type}
                    color={notificationTypeColor(sig.notification_type)}
                    variant="outlined"
                  />
                )}
                {sig.signal_type && sig.signal_type !== sig.notification_type && (
                  <Chip size="small" label={sig.signal_type} variant="outlined" />
                )}
                {sig.chain && <Chip size="small" label={sig.chain} variant="outlined" />}
              </Box>
            </Box>
            <Chip label={hit._score != null ? hit._score.toFixed(2) : '-'} size="small" variant="outlined" color="primary" />
          </Box>

          {(token.symbol || token.address) && (
            <Paper elevation={0} sx={{ p: 1.5, mb: 2, backgroundColor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Token</Typography>
              <MetricRow label="Symbol" value={token.symbol || '-'} />
              <MetricRow label="Address" value={token.address || '-'} />
              <MetricRow label="Current Price" value={formatCurrency(token.current_price_usd)} />
              <MetricRow label="Price Change" value={token.price_change_pct != null ? `${formatNumber(token.price_change_pct)}%` : '-'} />
              {token.first_smart_entry_price != null && (
                <MetricRow label="First Smart Entry" value={formatCurrency(token.first_smart_entry_price)} />
              )}
            </Paper>
          )}

          {(summary.trader_count != null || summary.total_usd_inflow != null || summary.highest_rank != null) && (
            <Paper elevation={0} sx={{ p: 1.5, mb: 2, backgroundColor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Summary</Typography>
              <MetricRow label="Trader Count" value={formatNumber(summary.trader_count, 0)} />
              <MetricRow label="Total USD Inflow" value={formatCurrency(summary.total_usd_inflow)} />
              <MetricRow label="Highest Rank" value={formatNumber(summary.highest_rank, 0)} />
            </Paper>
          )}

          {traders.length > 0 && (
            <Paper elevation={0} sx={{ p: 1.5, mb: 2, backgroundColor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Traders ({traders.length})
              </Typography>
              {traders.slice(0, 5).map((t, idx) => (
                <Box key={t.wallet || idx} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, gap: 1 }}>
                  <Typography variant="caption" sx={{ wordBreak: 'break-all', flex: 1 }}>
                    {t.wallet || '-'}{t.leaderboard_rank != null ? ` (rank ${t.leaderboard_rank})` : ''}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    {formatCurrency(t.trade_size_usd)} · WR {formatPercent(t.win_rate != null ? t.win_rate * 100 : null)}
                  </Typography>
                </Box>
              ))}
            </Paper>
          )}

          {reasons.length > 0 && (
            <Paper elevation={0} sx={{ p: 1.5, mb: 2, backgroundColor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Why</Typography>
              {reasons.map((r, idx) => (
                <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                  • {r.text || JSON.stringify(r)}
                </Typography>
              ))}
            </Paper>
          )}

          <Paper elevation={0} sx={{ p: 1.5, backgroundColor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Timestamps</Typography>
            <MetricRow label="Triggered" value={formatDate(sig.timestamp)} />
            <MetricRow label="Sent" value={formatDate(sig.sent_at)} />
          </Paper>

          {sig.signal_id && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', wordBreak: 'break-all', fontSize: '0.7rem' }}>
                {sig.cta?.token_address ? (
                  <Link href={`https://dexscreener.com/${sig.chain || ''}/${sig.cta.token_address}`} target="_blank" rel="noopener noreferrer" sx={{ color: 'inherit', textDecoration: 'underline' }}>
                    {sig.signal_id}
                  </Link>
                ) : (
                  sig.signal_id
                )}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </ListItem>
  );
};

export default AlphaNotificationCard;
